import { useCallback } from 'react';
import dayjs from 'dayjs';
import type { AgentSessionEnrichedRow, AgentSessionsRow as AgentSessionRow } from '@/types/appRouter';
import { InlineEditableTitle } from './InlineEditableTitle';
import { SessionStatusDot } from './SessionStatusDot';
import Dropdown from '@/components/UI/Dropdown';
import { FaThumbtack } from 'react-icons/fa';
import { HiOutlineDotsVertical } from 'react-icons/hi';

function isEnrichedSession(s: AgentSessionRow | AgentSessionEnrichedRow): s is AgentSessionEnrichedRow {
    return 'activeRunStatus' in s;
}

interface ChatSessionItemProps {
    autopilotPlanId?: number | null;
    isSelected: boolean;
    onArchive: (sessionId: number) => void;
    onPin: (sessionId: number) => void;
    onRegenerateTitle: (sessionId: number) => void;
    onRename: (sessionId: number, title: string) => void;
    onSelect: (sessionId: number) => void;
    onUnpin: (sessionId: number) => void;
    session: AgentSessionRow | AgentSessionEnrichedRow;
}

export function ChatSessionItem({
    autopilotPlanId,
    isSelected,
    onArchive,
    onPin,
    onRegenerateTitle,
    onRename,
    onSelect,
    onUnpin,
    session
}: ChatSessionItemProps) {
    const title = (session.title ?? '').trim() || `Chat #${session.id}`;
    const isPinned = session.isPinned === 1;
    const timeAgo = session.updatedAt ? dayjs(session.updatedAt).fromNow() : '';
    const enriched = isEnrichedSession(session) ? session : null;
    const activeRunStatus = enriched?.activeRunStatus ?? null;
    const lastMessageSnippet = enriched?.lastMessageSnippet ?? null;

    const handleRename = useCallback(
        (newTitle: string) => {
            onRename(session.id, newTitle);
        },
        [onRename, session.id]
    );

    const menuButton = (
        <span aria-label="Session actions" className="text-zinc-400 hover:text-white">
            <HiOutlineDotsVertical className="h-4 w-4" />
        </span>
    );

    return (
        <div
            aria-label={title}
            className={`group flex items-start gap-2 px-3 py-2 border-b border-zinc-800 cursor-pointer hover:bg-zinc-800/40 ${isSelected ? 'bg-zinc-800/60' : ''}`}
            role="button"
            tabIndex={0}
            onClick={() => onSelect(session.id)}
            onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onSelect(session.id);
                }
            }}
        >
            <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5">
                    {isPinned && <FaThumbtack className="h-3 w-3 text-blue-400 shrink-0" />}
                    <SessionStatusDot status={activeRunStatus} />
                    <InlineEditableTitle className="text-sm text-white" value={title} onSave={handleRename} />
                    {autopilotPlanId != null && (
                        <span className="shrink-0 rounded bg-fuchsia-900/60 px-1 py-0.5 text-[9px] font-medium text-fuchsia-300 uppercase leading-none">
                            AP
                        </span>
                    )}
                </div>
                <div className="flex items-center gap-2 mt-0.5">
                    <span className="text-xs text-zinc-500">{timeAgo}</span>
                </div>
                {lastMessageSnippet && <div className="text-xs text-zinc-400 truncate mt-0.5">{lastMessageSnippet}</div>}
            </div>

            <div className="opacity-0 group-hover:opacity-100 shrink-0 mt-0.5" role="presentation" onClick={(e) => e.stopPropagation()}>
                <Dropdown hideChevron buttonClassName="!p-1 !rounded" buttonContent={menuButton} position="right">
                    {isPinned ? (
                        <button
                            className="w-full text-left px-3 py-1.5 text-sm text-zinc-300 hover:bg-zinc-700"
                            type="button"
                            onClick={() => onUnpin(session.id)}
                        >
                            Unpin
                        </button>
                    ) : (
                        <button
                            className="w-full text-left px-3 py-1.5 text-sm text-zinc-300 hover:bg-zinc-700"
                            type="button"
                            onClick={() => onPin(session.id)}
                        >
                            Pin
                        </button>
                    )}
                    <button
                        className="w-full text-left px-3 py-1.5 text-sm text-zinc-300 hover:bg-zinc-700"
                        type="button"
                        onClick={() => onRegenerateTitle(session.id)}
                    >
                        Regenerate title
                    </button>
                    <button
                        className="w-full text-left px-3 py-1.5 text-sm text-red-400 hover:bg-zinc-700"
                        type="button"
                        onClick={() => onArchive(session.id)}
                    >
                        Archive
                    </button>
                </Dropdown>
            </div>
        </div>
    );
}
